import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSound } from "@/lib/SoundContext";
import { SITE, ABOUT, CONTACT } from "@/data/content";
import { Polaroid } from "@/components/Bits";

const mini = { fontFamily: "'Kalam',cursive" };

function DoorButton({ testid, to, className, rotate = 0, style = {}, children }) {
  const { play } = useSound();
  const navigate = useNavigate();
  return (
    <motion.button
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        play("pop");
        navigate(to);
      }}
      className={`absolute text-left cursor-pointer ${className}`}
      style={{ ...style, rotate }}
      whileHover={{ rotate: [rotate, rotate + 4, rotate - 2, rotate + 1, rotate], transition: { duration: 0.45 } }}
    >
      {children}
    </motion.button>
  );
}

function Doodle({ testid, className, rotate = 0, children }) {
  return (
    <motion.div
      data-testid={testid}
      className={`absolute pointer-events-none ${className}`}
      style={{ rotate }}
    >
      {children}
    </motion.div>
  );
}

// hand-drawn pointer: text + curved arrow, always on, gently bobbing
function PointerNote({ testid, text, className, flip = false, rotate = 0 }) {
  return (
    <motion.div
      data-testid={testid}
      className={`absolute pointer-events-none flex items-end gap-1 ${className}`}
      style={{ rotate }}
      animate={{ y: [0, -3, 0] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
    >
      <span className="font-hand font-bold text-[10px] sm:text-[11px] text-ink leading-tight" style={mini}>
        {text}
      </span>
      <svg viewBox="0 0 40 34" className={`w-7 flex-none ${flip ? "-scale-x-100" : ""}`} aria-hidden="true">
        <path d="M4 4 q22 -2 26 18" fill="none" stroke="#2B2320" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M26 14 l6 8 l-9 -1" fill="none" stroke="#2B2320" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </motion.div>
  );
}

// squiggly line that mimics handwriting — for magnets without real info
function Squig({ w = "92%" }) {
  return (
    <svg viewBox="0 0 60 6" preserveAspectRatio="none" style={{ width: w, height: 5, display: "block", marginTop: 3 }} aria-hidden="true">
      <path d="M0 3 q3.75 -4 7.5 0 t7.5 0 t7.5 0 t7.5 0 t7.5 0 t7.5 0 t7.5 0 t7.5 0" fill="none" stroke="#2B2320" strokeOpacity="0.32" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}


function LondonBus() {
  return (
    <svg viewBox="0 0 120 82" className="w-full drop-shadow-[0_3px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
      <g stroke="#2B2320" strokeLinejoin="round" strokeLinecap="round">
        <rect x="8" y="8" width="104" height="54" rx="11" fill="#D6352B" strokeWidth="4" />
        {[22, 42, 62, 82].map((x) => (
          <rect key={x} x={x} y="15" width="14" height="11" rx="2.5" fill="#CFE8F5" strokeWidth="3" />
        ))}
        <line x1="8" y1="34" x2="112" y2="34" strokeWidth="3" />
        {[22, 42, 62].map((x) => (
          <rect key={"l" + x} x={x} y="41" width="14" height="11" rx="2.5" fill="#CFE8F5" strokeWidth="3" />
        ))}
        <rect x="86" y="41" width="18" height="11" rx="2.5" fill="#F2B705" strokeWidth="3" />
        <circle cx="34" cy="64" r="10" fill="#3A3446" strokeWidth="4" />
        <circle cx="88" cy="64" r="10" fill="#3A3446" strokeWidth="4" />
        <circle cx="34" cy="64" r="3.5" fill="#9AA1AB" strokeWidth="2.5" />
        <circle cx="88" cy="64" r="3.5" fill="#9AA1AB" strokeWidth="2.5" />
        <rect x="8" y="38" width="9" height="18" rx="2.5" fill="#F2B705" strokeWidth="3" />
      </g>
    </svg>
  );
}

export default function DoorFront({ phase, onOpen }) {
  const { play } = useSound();
  const opening = phase === "opening";
  return (
    <motion.div
      className="absolute inset-0 rounded-[32px] border-[5px] border-ink fridge-door-face z-30 overflow-hidden cursor-pointer"
      style={{ transformOrigin: "left center" }}
      initial={false}
      animate={opening ? { rotateY: [0, -16, -98, -112, -104], x: [0, 0, 3, 4, 2] } : { rotateY: 0, x: 0 }}
      transition={opening ? { duration: 1.15, times: [0, 0.18, 0.55, 0.8, 1], ease: "easeInOut" } : { duration: 0.4 }}
      onClick={onOpen}
      role="button"
      aria-label="Open the fridge"
      data-testid="fridge-door"
      whileHover={phase === "closed" ? { x: 2 } : undefined}
    >
      {/* freezer strip */}
      <div className="absolute top-0 inset-x-0 h-[30%] border-b-[5px] border-ink">
        <span className="absolute right-3 bottom-1.5 font-hand text-[11px] text-ink/45" style={mini}>FREEZER</span>
        {/* little stickers filling the freezer strip gaps */}
        <Doodle testid="door-sticker-donut" className="left-[7%] top-[58%] w-8" rotate={-12}>
          <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="#FF8FB1" stroke="#2B2320" strokeWidth="2" />
            <circle cx="12" cy="12" r="3.5" fill="#F6EDE0" stroke="#2B2320" strokeWidth="2" />
            <path d="M6 8 l1.5 1 M15 5.5 l1 1.5 M17 12 l1.6 .8 M7 15.5 l1.4 1" stroke="#FFF3D6" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </Doodle>
        <Doodle testid="door-sticker-milk" className="right-[5%] top-[34%] w-7" rotate={9}>
          <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
            <path d="M8 3 h8 v4 l2 4 v10 h-12 v-10 l2 -4 z" fill="#FFFDF6" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
            <path d="M8 3 h8 v4 h-8 z" fill="#38BDF8" stroke="#2B2320" strokeWidth="2" />
            <path d="M6 15 h12" stroke="#38BDF8" strokeWidth="2.5" />
          </svg>
        </Doodle>
        <Doodle testid="door-sticker-banana" className="left-[31%] top-[72%] w-8" rotate={-18}>
          <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
            <path d="M4 5 q2 12 15 14 l2 -2 q-11 -2 -14 -13 z" fill="#FFE66D" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
            <circle cx="4.5" cy="4.5" r="1.8" fill="#8B5E34" stroke="#2B2320" strokeWidth="1.5" />
          </svg>
        </Doodle>
        {/* takeout menus pinned with a magnet — vertical like real ones */}
        <Doodle testid="door-menus" className="left-[5%] top-[8%] w-[40%] h-[19%]" rotate={0}>
          <div className="relative w-full h-full">
            <div className="absolute left-0 top-0 w-[47%] bg-white border-[2.5px] border-ink/75 rounded-[4px] -rotate-6 shadow-[0_3px_0_rgba(43,35,32,0.2)]">
              <div className="bg-[#D6352B] px-1 py-1">
                <p className="font-display text-[7px] text-white leading-none text-center">WOK THIS WAY</p>
              </div>
              <div className="px-1.5 py-1">
                <p className="font-hand text-[7px] text-ink/60 leading-snug" style={mini}>chinese takeaway</p>
                <Squig w="90%" />
                <Squig w="100%" />
                <Squig w="75%" />
                <Squig w="95%" />
              </div>
            </div>
            <div className="absolute right-0 top-2 w-[47%] bg-white border-[2.5px] border-ink/75 rounded-[4px] rotate-3 shadow-[0_3px_0_rgba(43,35,32,0.2)]">
              <div className="bg-[#E8760C] px-1 py-1">
                <p className="font-display text-[7px] text-white leading-none text-center">TANDOORI NIGHTS</p>
              </div>
              <div className="px-1.5 py-1">
                <p className="font-hand text-[7px] text-ink/60 leading-snug" style={mini}>indian takeaway</p>
                <Squig w="85%" />
                <Squig w="100%" />
                <Squig w="70%" />
                <Squig w="90%" />
              </div>
            </div>
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#38BDF8] border-[2.5px] border-ink z-10" />
          </div>
        </Doodle>
        <Doodle testid="door-magnets" className="right-[10%] top-[14%] flex gap-1" rotate={2}>
          {["E", "N", "A"].map((l, i) => (
            <span key={i} className="w-6 h-6 rounded-full border-[2.5px] border-ink flex items-center justify-center font-bold text-[11px]"
              style={{ background: ["#FF5964", "#FFE66D", "#38B000"][i], fontFamily: "'Baloo 2'" }}>
              {l}
            </span>
          ))}
        </Doodle>

        {/* the London project — souvenir bus magnet, bobbing proudly */}
        <PointerNote testid="london-pointer" text="click here to see my most fav projects" className="left-[47%] top-[11%] w-[30%] flex-col !items-start" rotate={2} />
        <motion.a
          href={SITE.londonProject}
          target="_blank"
          rel="noreferrer"
          data-testid="door-london-magnet"
          className="absolute left-[43%] top-[23%] w-[32%] cursor-pointer"
          style={{ rotate: 3 }}
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ rotate: [3, 7, 1, 4, 3], scale: 1.08, transition: { duration: 0.45 } }}
          onClick={(e) => {
            e.stopPropagation();
            play("pop");
          }}
        >
          <LondonBus />
          <p className="font-hand font-bold text-[8px] sm:text-[9px] text-center text-ink leading-tight mt-0.5" style={mini}>
            THE LONDON PROJECT
          </p>
        </motion.a>
      </div>

      {/* name plate */}
      <div className="absolute top-[33.5%] left-1/2 -translate-x-1/2 bg-[#FFFDF6] border-[3px] border-ink rounded-lg px-3 py-1 shadow-[0_3px_0_rgba(43,35,32,0.25)] -rotate-1 text-center z-10">
        <p className="font-display text-sm sm:text-base leading-tight text-ink whitespace-nowrap">{SITE.name}</p>
        <p className="font-hand text-[9px] sm:text-[10px] tracking-[0.2em] text-ink/60" style={mini}>{SITE.tagline.toUpperCase()}</p>
      </div>

      {/* chrome latch handles — freezer + main door (right edge, like the retro fridge) */}
      {[
        { id: "freezer", top: "13%" },
        { id: "main", top: "38%" },
      ].map((h) => (
        <motion.div
          key={h.id}
          data-testid={`fridge-latch-${h.id}`}
          className="absolute right-[3.5%] w-[15%] h-[4.2%]"
          style={{ top: h.top }}
          animate={opening ? { x: [0, 7, 4, 8, 5] } : { x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full bg-[#B8BFC9] border-[2.5px] border-ink" />
          <div className="absolute right-0 top-0 w-[86%] h-full rounded-full chrome-latch border-[2.5px] border-ink shadow-[0_3px_0_rgba(43,35,32,0.3)]" />
          <div className="absolute right-[12%] top-[18%] w-[58%] h-[24%] rounded-full bg-white/85 pointer-events-none" />
        </motion.div>
      ))}

      {/* about note — big vibrant magnet, impossible to miss */}
      <PointerNote testid="about-pointer" text="click to read me" className="left-[8%] top-[38.5%] w-[24%]" rotate={-4} />
      <DoorButton testid="door-note-about" to="/about" className="left-[6%] top-[44%] w-[32%] border-[3px] border-ink rounded-xl p-2.5 shadow-[0_5px_0_rgba(43,35,32,0.3)] overflow-hidden" rotate={-3}
        style={{ background: "linear-gradient(135deg,#FF5964 0%,#FF7A45 55%,#FFB25C 100%)" }}>
        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#FFE66D] border-[2.5px] border-ink shadow-[0_2px_0_rgba(43,35,32,0.35)]" />
        <svg viewBox="0 0 20 20" className="absolute right-1.5 top-1.5 w-4" aria-hidden="true">
          <path d="M10 1 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill="#FFF3D6" stroke="#2B2320" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
        <svg viewBox="0 0 20 20" className="absolute left-1.5 bottom-1.5 w-3 opacity-90" aria-hidden="true">
          <path d="M10 1 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill="#FFF3D6" stroke="#2B2320" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
        <p className="font-display text-[14px] sm:text-[16px] leading-tight text-white drop-shadow-[0_2px_0_rgba(43,35,32,0.45)]">GET TO KNOW ME</p>
        <p className="font-hand text-[10px] text-white/90 italic" style={mini}>you know you want to</p>
      </DoorButton>

      {/* grocery list note — IMPORTANT info, up top where eyes land first */}
      <PointerNote testid="contact-pointer" text="how to reach me!" className="right-[9%] top-[35.5%] w-[20%]" rotate={4} flip />
      <DoorButton testid="door-note-contact" to="/contact" className="right-[11%] top-[42%] w-[30%] bg-white border-[3px] border-ink rounded-md shadow-[0_5px_0_rgba(43,35,32,0.3)] overflow-hidden" rotate={2}>
        <div className="bg-[#E8402F] px-2 py-1 flex items-center justify-between">
          <p className="font-display text-[10px] sm:text-[11px] text-white leading-none">{CONTACT.heading}</p>
          <svg viewBox="0 0 24 24" className="w-3.5" aria-hidden="true">
            <circle cx="12" cy="13" r="7" fill="#FFE66D" stroke="#2B2320" strokeWidth="2" />
            <path d="M12 6 q1 -4 4 -4" fill="none" stroke="#2B2320" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 6 q4 -3 6 0 q-3 2 -6 0" fill="#38B000" stroke="#2B2320" strokeWidth="1.5" />
          </svg>
        </div>
        <div className="p-2">
          {CONTACT.items.slice(0, 3).map((t, i) => (
            <p key={i} className="font-hand text-[9px] text-ink/60 border-b border-dashed border-ink/25" style={mini}>☐ {t}</p>
          ))}
          <p className="font-hand text-[9px] font-bold text-tomato" style={mini}>tap to open →</p>
        </div>
      </DoorButton>

      {/* dal makhani recipe card — fun reading, bottom left */}
      <Doodle testid="door-menu" className="left-[7%] bottom-[4%] w-[29%] paper border-[3px] border-ink rounded-md p-2" rotate={-3}>
        <p className="font-display text-[10px] leading-tight">DAL MAKHANI</p>
        <p className="font-hand text-[8px] text-ink/55" style={mini}>my comfort food, basically:</p>
        <div className="mt-1">
          <Squig w="96%" />
          <Squig w="88%" />
          <Squig w="100%" />
          <Squig w="72%" />
          <Squig w="90%" />
        </div>
      </Doodle>

      {/* polaroids + stickers — filling the empty middle-right of the door */}
      <Polaroid label="thali day" src={SITE.photos.doorA} className="absolute right-[5%] bottom-[4%] w-[24%] aspect-square -rotate-3" />
      <Polaroid label="chai o'clock" src={SITE.photos.doorB} className="absolute right-[31%] bottom-[8%] w-[19%] aspect-square rotate-6" />
      <Polaroid label="pizza night" src={SITE.photos.doorC} className="absolute right-[3%] top-[63%] w-[21%] aspect-square rotate-2" />
      <Polaroid label="hi shona!" src={SITE.photos.doorD} className="absolute left-[40%] top-[67%] w-[16%] aspect-square -rotate-6" />
      <Doodle testid="door-sticker" className="left-[42%] bottom-[34%] w-12 h-12 rounded-full bg-[#FF5964] border-[3px] border-ink flex items-center justify-center text-center" rotate={-8}>
        <span className="font-hand text-[7.5px] font-bold text-white leading-tight" style={mini}>100% ORGANIC<br />CHAOS</span>
      </Doodle>
      {/* little vinyl-style stickers scattered in the gaps */}
      <Doodle testid="door-sticker-star" className="left-[35%] top-[57%] w-8" rotate={12}>
        <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
          <path d="M12 2 l2.6 6.6 7 .6 -5.3 4.6 1.6 6.9 -5.9 -3.7 -5.9 3.7 1.6 -6.9 -5.3 -4.6 7 -.6 z" fill="#FFE66D" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Doodle>
      {/* extra stickers filling the gap between the two left magnets */}
      <Doodle testid="door-sticker-heart" className="left-[11%] top-[59%] w-7" rotate={-14}>
        <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
          <path d="M12 21 q-8 -6 -9 -11 a5 5 0 0 1 9 -3 a5 5 0 0 1 9 3 q-1 5 -9 11 z" fill="#FF5964" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Doodle>
      <Doodle testid="door-sticker-cherry" className="left-[24%] top-[68%] w-7" rotate={10}>
        <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
          <path d="M15 3 q-1 6 -6 9 M15 3 q3 4 4 9" fill="none" stroke="#2B2320" strokeWidth="2" strokeLinecap="round" />
          <path d="M15 3 q3 -2 5 0" fill="none" stroke="#38B000" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="8" cy="15" r="4.5" fill="#D6352B" stroke="#2B2320" strokeWidth="2" />
          <circle cx="19" cy="14" r="4" fill="#D6352B" stroke="#2B2320" strokeWidth="2" />
        </svg>
      </Doodle>
      <Doodle testid="door-sticker-smiley" className="right-[31%] top-[66%] w-8" rotate={-10}>
        <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="#38BDF8" stroke="#2B2320" strokeWidth="2" />
          <circle cx="8.5" cy="10" r="1.4" fill="#2B2320" />
          <circle cx="15.5" cy="10" r="1.4" fill="#2B2320" />
          <path d="M7.5 14 q4.5 4 9 0" fill="none" stroke="#2B2320" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </Doodle>
      <Doodle testid="door-sticker-bolt" className="left-[52%] top-[84%] w-7" rotate={18}>
        <svg viewBox="0 0 24 24" className="drop-shadow-[0_2px_0_rgba(43,35,32,0.3)]" aria-hidden="true">
          <path d="M13 2 L5 13 h5 l-2 9 9 -12 h-5 z" fill="#FF8A5C" stroke="#2B2320" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Doodle>
    </motion.div>
  );
}
